import { useState, useRef, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'
import { apiUrl, authFetch } from '../../lib/api'

interface Result { path: string; line: number; text: string }

export function SearchPanel() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Result[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()
  const { openTab } = useAppStore()

  const doSearch = useCallback((q: string) => {
    if (!q.trim()) { setResults([]); setSearched(false); return }
    setLoading(true)
    authFetch(apiUrl(`/api/files/search?q=${encodeURIComponent(q)}`))
      .then((r) => r.json())
      .then((data) => { setResults(data); setSearched(true) })
      .catch(() => setResults([]))
      .finally(() => setLoading(false))
  }, [])

  const onChange = (val: string) => {
    setQuery(val)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => doSearch(val), 300)
  }

  const openResult = (r: Result) => {
    authFetch(apiUrl(`/api/files/read?path=${encodeURIComponent(r.path)}`))
      .then((res) => res.json())
      .then((data) => {
        openTab({
          id: r.path, path: `/${r.path}`, name: r.path.split('/').pop() ?? r.path,
          language: r.path.split('.').pop() ?? 'text', content: data.content ?? '',
        })
      })
      .catch(() => {})
  }

  // Group results by file
  const grouped = new Map<string, Result[]>()
  for (const r of results) {
    if (!grouped.has(r.path)) grouped.set(r.path, [])
    grouped.get(r.path)!.push(r)
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }}>
      <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        SEARCH
      </div>
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <input
          value={query}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') doSearch(query) }}
          placeholder="Search in files…"
          style={{
            width: '100%', padding: '6px 10px', borderRadius: 6,
            border: '1px solid var(--border-2)', background: 'var(--bg-2)',
            color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 12, outline: 'none',
          }}
          autoFocus
        />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {loading && <div style={{ padding: 12, color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>Searching…</div>}
        {!loading && searched && results.length === 0 && (
          <div style={{ padding: 12, color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>No results found</div>
        )}
        {!loading && [...grouped.entries()].map(([filePath, matches]) => (
          <div key={filePath}>
            <div style={{ padding: '6px 14px', fontSize: 11, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10 }}>📄</span>
              {filePath}
              <span style={{ fontSize: 9, color: 'var(--text-2)', marginLeft: 'auto' }}>{matches.length}</span>
            </div>
            {matches.map((r, i) => (
              <div
                key={`${r.path}:${r.line}:${i}`}
                onClick={() => openResult(r)}
                style={{
                  padding: '4px 14px 4px 30px', cursor: 'pointer', fontSize: 11,
                  fontFamily: 'var(--font-mono)', color: 'var(--text-1)',
                  display: 'flex', gap: 8, alignItems: 'baseline',
                  transition: 'background 0.08s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-3)' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
              >
                <span style={{ color: 'var(--text-2)', fontSize: 10, minWidth: 28, textAlign: 'right', flexShrink: 0 }}>L{r.line}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{highlightMatch(r.text, query)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function highlightMatch(text: string, query: string) {
  if (!query) return text
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ background: 'rgba(245,161,87,0.3)', color: 'var(--accent-3)', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </span>
      {text.slice(idx + query.length)}
    </>
  )
}
